import { StyleDeclarationValue, css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { EyeIcon } from '../../../../web/components/svgs/icons/EyeIcon';
import { XIcon } from '../../../../web/components/svgs/icons/XIcon';
import { Button } from '../../../../web/components-v2/Button';
import { Input } from '../../../../web/components-v2/Input';
import { Label } from '../../../../web/components-v2/Label';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { useGamification } from '../../../hooks';
import { useResponseHandler } from '../../../hooks/error';
import { useInput } from '../../../hooks/input';
import { MemeViewModel } from '../../../viewModels/memes';
import { MemeFile } from '../MemeFile';
import { styleSheet } from './styles';

interface IProps {
	meme: MemeViewModel;
	onDeleteClick?(id: string): () => void;
	className?: string;
	styles?: StyleDeclarationValue[];
}

export const Meme: React.FC<IProps> = observer(props => {
	const { className, meme, onDeleteClick, styles = [] } = props;
	const [phrase, , setPhrase] = useInput(meme.phrase);
	const gamification = useGamification();
	const [updatePhraseResponseHandler] = useResponseHandler('UpdatePhrase-Error', "Unable to update meme's text");
	const [updateImageResponseHandler] = useResponseHandler('UpdateImage-Error', "Unable to update meme's image");
	const [updateSoundResponseHandler] = useResponseHandler('UpdateSound-Error', "Unable to update meme's sound");

	const savePhrase = () => {
		meme
			.updatePhrase(phrase)

			.then(() => toast.success('Text updated'))
			.catch(updatePhraseResponseHandler);
	};

	const onPreview = () => {
		gamification.setMeme(meme);
	};

	const onImageSelected = (file: File) => {
		meme
			.updateImage(file)

			.then(() => toast.success('Image updated'))
			.catch(updateImageResponseHandler);
	};

	const onSoundSelected = (file: File) => {
		meme
			.updateSound(file)

			.then(() => toast.success('Sound updated'))
			.catch(updateSoundResponseHandler);
	};

	if (meme.isBusy) {
		return (
			<div className={`${css(styleSheet.container, ...styles)} meme ${className || ''}`}>
				<div className={css(baseStyleSheet.flexCenter, styleSheet.loader)}>
					<LoadingSpinner type='small' />
				</div>
			</div>
		);
	}

	return (
		<div className={`${css(styleSheet.container, ...styles)} meme ${className || ''}`}>
			<div className={css(styleSheet.deleteContainer)}>
				<Button variant='ghost' size='icon' aria-label='Delete meme' onClick={onDeleteClick(meme.id)}>
					<XIcon height={16} width={16} />
				</Button>
			</div>
			<div className={css(styleSheet.fileContainer)}>
				<MemeFile label='Image:' url={meme.image?.url} onFileSelected={onImageSelected} accept='.png' />
				<MemeFile label='Sound:' url={meme.sound?.url} onFileSelected={onSoundSelected} accept='.mp3' />
			</div>
			<div className={css(styleSheet.textContainer)}>
				<Label htmlFor={meme.id} styles={[styleSheet.textLabel]}>
					Text:
				</Label>
				<Input styles={[styleSheet.input]} id={meme.id} type='text' value={phrase} onChange={setPhrase} />
				<Button size='sm' onClick={savePhrase}>
					Save
				</Button>
				<Button variant='ghost' size='icon' aria-label='Preview meme' onClick={onPreview}>
					<EyeIcon fillColor='currentColor' />
				</Button>
			</div>
		</div>
	);
});
